import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Paper,
  Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Button, Drawer } from '../../../../../../../ui';
import { generateReportPDF, getReport } from '../../../../../../../utils/api_service/SmartInterview';
import LoadingSpinner from '../../../../ui/LoaderSpinner';

export type GenerateReportProps = {
  isOpen: boolean;
  onCloseDrawer: () => void;
  userIds: string[];
  interviewRoundId: string | undefined;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export const GenerateReport: React.FC<GenerateReportProps> = ({
  isOpen,
  onCloseDrawer,
  userIds,
  interviewRoundId,
  loading,
  setLoading,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  // English keys sent to API — do not translate these
  const columnLabels = [
    'Summary',
    'Question and Answer',
    'Transcript',
    'Technical Skills',
    'Industry Skills',
    'Product Knowledge',
    'Communication',
    'Problem Solving',
    'Leadership',
    'Team Collaboration',
    'Suggested Questions',
    'Interview Type',
  ];

  // Display labels mapped from English keys
  const columnLabelTranslations: Record<string, string> = {
    'Summary': t(SI.results.report.sections.summary),
    'Question and Answer': t(SI.results.report.sections.questionAndAnswer),
    'Transcript': t(SI.results.report.sections.transcript),
    'Technical Skills': t(SI.results.report.sections.technicalSkills),
    'Industry Skills': t(SI.results.report.sections.industrySkills),
    'Product Knowledge': t(SI.results.report.sections.productKnowledge),
    'Communication': t(SI.results.report.sections.communication),
    'Problem Solving': t(SI.results.report.sections.problemSolving),
    'Leadership': t(SI.results.report.sections.leadership),
    'Team Collaboration': t(SI.results.report.sections.teamCollaboration),
    'Suggested Questions': t(SI.results.report.sections.suggestedQuestions),
    'Interview Type': t(SI.results.report.sections.interviewType),
  };

  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(
    columnLabels.reduce((acc, label) => ({ ...acc, [label]: true }), {}),
  );

  const [reportId, setReportId] = useState<string>();
  const [pdfURL, setPdfURL] = useState<string>();

  const handleCheckboxChange = (label: string) => {
    setCheckedItems((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  useEffect(() => {
    setLoading(true);
    const poll = async () => {
      if (!interviewRoundId || !reportId) {
        setLoading(false);
        return;
      }

      try {
        const response = await getReport(interviewRoundId, reportId);
        const responseData = await response.json();

        if (response.ok) {
          if (responseData.status === 'SUCCESS') {
            toast.success(t(SI.results.report.toasts.generated), {
              position: 'bottom-center',
            });
            setPdfURL(responseData.pdfFilename);
            setLoading(false);
            return;
          }

          setTimeout(poll, 4000);
        } else {
          toast.error(t(SI.results.report.toasts.failedStatus), {
            position: 'bottom-center',
          });
          setLoading(true);
        }
      } catch (err) {
        setTimeout(poll, 4000);
      }
    };

    poll();
  }, [interviewRoundId, reportId]);

  const handleGenerate = async () => {
    if (!interviewRoundId) {
      toast.error(t(SI.results.report.toasts.missingRoundId), {
        position: 'bottom-center',
      });
      return;
    }

    const selectedLabels = Object.entries(checkedItems)
      .filter(([_, checked]) => checked)
      .map(([label]) => label);

    try {
      const response = await generateReportPDF(userIds, selectedLabels, interviewRoundId);
      const responseData = await response.json();

      if (response.ok) {
        setReportId(responseData.id);
      } else {
        toast.success(t(SI.results.report.toasts.generated), {
          position: 'bottom-center',
        });
        setLoading(false);
      }
    } catch (err) {
      toast.error(t(SI.results.report.toasts.failedGenerate), {
        position: 'bottom-center',
      });
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      footerContent={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2.5, width: '100%' }}>
          <Button label={t(SI.results.report.generate)} onClick={handleGenerate} variant='primary' />
          <Button label={t(SI.results.report.close)} onClick={onCloseDrawer} variant='secondary' />
        </Box>
      }
      title={t(SI.results.report.title)}
      width='w-full md:w-2/5'
    >
      <Stack spacing={2} sx={{ p: 3 }}>
        <Box>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {t(SI.results.report.heading)}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t(SI.results.report.subtitle)}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {columnLabels.map((label) => (
            <Grid item xs={12} sm={6} key={label}>
              <Paper
                variant='outlined'
                onClick={() => handleCheckboxChange(label)}
                sx={{
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderRadius: 1,
                  borderColor: checkedItems[label] ? 'primary.main' : 'divider',
                  bgcolor: checkedItems[label] ? 'action.selected' : 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Checkbox
                  checked={checkedItems[label]}
                  onChange={() => handleCheckboxChange(label)}
                  size='small'
                  sx={{ p: 0.5, mr: 1 }}
                />
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {columnLabelTranslations[label] ?? label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '15vh' }}>
            <LoadingSpinner label='' />
          </Box>
        ) : (
          pdfURL && (
            <Box sx={{ pt: 1 }}>
              <Link
                href={pdfURL}
                download
                target='_blank'
                rel='noopener noreferrer'
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  fontWeight: 500,
                }}
              >
                {t(SI.results.report.download)}
              </Link>
            </Box>
          )
        )}
      </Stack>
    </Drawer>
  );
};

export default GenerateReport;
